/**
 * format.ts — the ONLY place that turns wire values into display strings.
 *
 * Money crosses the API as integer agorot (ARCHITECTURE §9); this module is the only
 * place that divides by 100. Number formatting follows spec-personal §8.4:
 *   `Intl.NumberFormat('he-IL')` on the ABSOLUTE value, then `₪` glued in front of the
 *   digits, then the sign (U+2212 MINUS / `+`) in front of the `₪` — never
 *   `style: 'currency'`, whose output ("‎-413 ₪") contradicts the convention.
 * Dates are `dd.mm.yyyy`.
 */

import { DAYS, MONTHS, MONTHS_SHORT } from './i18n/he.ts';

const MINUS = '−'; // U+2212 MINUS SIGN — never a hyphen
const SHEKEL = '₪';

const nfCache = new Map<number, Intl.NumberFormat>();
function nf(decimals: number): Intl.NumberFormat {
  let f = nfCache.get(decimals);
  if (!f) {
    f = new Intl.NumberFormat('he-IL', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    nfCache.set(decimals, f);
  }
  return f;
}

/**
 * Plain number with he-IL thousands separators.
 *
 * Negatives carry U+2212 like every other signed figure (spec §8.4): `Intl` on its own emits
 * "‎-630,000" (LRM + hyphen), which is what the net-worth chart's y-axis showed while the numbers
 * beside it used the real minus.
 */
export function fmtNum(n: number, decimals = 0): string {
  const body = nf(decimals).format(Math.abs(n));
  return n < 0 ? MINUS + body : body;
}

/**
 * `fmtMoney(agorot)` → `₪1,234` · `fmtMoney(-41260, 2)` → `−₪412.60` · `plus` adds `+` on positives.
 * `agorot` is an integer; `null`/`undefined` renders as an em dash.
 */
export function fmtMoney(agorot: number | null | undefined, decimals = 0, plus = false): string {
  if (agorot == null || !Number.isFinite(agorot)) return '—';
  const value = agorot / 100;
  const body = SHEKEL + nf(decimals).format(Math.abs(value));
  if (value < 0) return MINUS + body;
  if (plus && value > 0) return '+' + body;
  return body;
}

/** `≈ ₪6,903` — forecasts and estimates (spec §4.4). */
export function fmtApprox(agorot: number | null | undefined, decimals = 0): string {
  if (agorot == null || !Number.isFinite(agorot)) return '—';
  return '≈ ' + fmtMoney(agorot, decimals);
}

/** Signed percentage: `+0.7%` / `−12.3%` (U+2212). */
export function fmtPct(pct: number | null | undefined, decimals = 1): string {
  if (pct == null || !Number.isFinite(pct)) return '—';
  const sign = pct < 0 ? MINUS : pct > 0 ? '+' : '';
  return sign + nf(decimals).format(Math.abs(pct)) + '%';
}

/** Unsigned percentage for meters: `33%`. */
export function fmtPctPlain(ratio: number | null | undefined, decimals = 0): string {
  if (ratio == null || !Number.isFinite(ratio)) return '—';
  return nf(decimals).format(ratio * 100) + '%';
}

/** A foreign original amount for the secondary line: `$42.00` / `EUR 12.00`. */
export function fmtForeign(minor: number | null | undefined, currency: string | null | undefined): string {
  if (minor == null || !currency) return '';
  const symbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency + ' ';
  return symbol + nf(2).format(Math.abs(minor) / 100);
}

/** `2026-09-06` → `06.09.2026`. */
export function fmtDate(day: string | null | undefined): string {
  if (!day) return '';
  const p = day.slice(0, 10).split('-');
  if (p.length !== 3) return day;
  return `${p[2]}.${p[1]}.${p[0]}`;
}

/** `2026-09-06` → `06.09`. */
export function fmtDM(day: string | null | undefined): string {
  if (!day) return '';
  const p = day.slice(0, 10).split('-');
  if (p.length !== 3) return day;
  return `${p[2]}.${p[1]}`;
}

/** `2026-09` → `09.2026` (a card's last installment month). */
export function fmtMonthNum(month: string | null | undefined): string {
  if (!month) return '';
  const p = month.slice(0, 7).split('-');
  if (p.length !== 2) return month;
  return `${p[1]}.${p[0]}`;
}

/** ISO timestamp → `06.09.2026 06:12` in the viewer's locale time zone. */
export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const rtf = new Intl.RelativeTimeFormat('he', { numeric: 'auto' });

/** "לפני רגע" / "לפני 3 שעות" / "לפני יומיים" — spec §2.4. */
export function relTime(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = (now.getTime() - then) / 1000;
  if (diff < 90) return 'לפני רגע';
  if (diff < 3600) return rtf.format(-Math.round(diff / 60), 'minute');
  if (diff < 86400) return rtf.format(-Math.round(diff / 3600), 'hour');
  return rtf.format(-Math.round(diff / 86400), 'day');
}

/** `2026-09` → `ספטמבר 2026` (or `ספטמבר` with `withYear = false`). */
export function monthName(month: string, withYear = true): string {
  const p = month.slice(0, 7).split('-');
  const idx = Number(p[1]) - 1;
  const name = MONTHS[idx] ?? month;
  return withYear ? `${name} ${p[0]}` : name;
}

/** `2026-09` → `ספט׳`. */
export function monthShort(month: string): string {
  const idx = Number(month.slice(5, 7)) - 1;
  return MONTHS_SHORT[idx] ?? month;
}

/** `2026-09-06` → `ראשון` (weekday name; the mockup's day headers). */
export function dayName(day: string): string {
  const [y, m, d] = day.slice(0, 10).split('-').map(Number);
  const date = new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
  return DAYS[date.getDay()] ?? '';
}

/** Avatar initial. */
export function initial(name: string): string {
  return (name || '?').trim().charAt(0);
}

/** Mask helper: `6516` → `***6516` (rendered LTR-isolated by the caller). */
export function mask(last4: string | null | undefined): string {
  return last4 ? `***${last4}` : '';
}

/** `Max` + `6044` → `Max ****6044` for card headers. */
export function cardMask(last4: string | null | undefined): string {
  return last4 ? `****${last4}` : '';
}
